/** Top application bar: file access, capture, ROI tools, history, zoom, panels and help. */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { busy, canRedo, canUndo, redo, tool, undo, type ToolId } from '../app/store';
import { captureState, freezeFrame, startCapture } from '../app/capture';
import { loadSessionFile, saveSession } from '../app/session';
import { pasteFromClipboard } from './globalInput';
import { snapPixels, viewportApi } from '../view/viewState';
import { openFilePicker } from './panels/SourcesPanel';
import { layout, openPanel, resetLayout, togglePanel } from './WindowManager';
import { PANEL_TITLES } from './panelRegistry';
import { embedImages } from './panels/ExportPanel';
import { Icon } from './icons';

const TOOLS: { id: ToolId; icon: string; key: string; label: string }[] = [
  { id: 'select', icon: 'select', key: 'V', label: 'Select / edit' },
  { id: 'pan', icon: 'pan', key: 'H', label: 'Pan' },
  { id: 'line', icon: 'line', key: 'L', label: 'Line profile' },
  { id: 'rect', icon: 'rect', key: 'R', label: 'Rectangle' },
  { id: 'ellipse', icon: 'ellipse', key: 'E', label: 'Ellipse' },
  { id: 'polygon', icon: 'polygon', key: 'P', label: 'Polygon' },
  { id: 'point', icon: 'point', key: 'O', label: 'Point / spot' },
  { id: 'calibrate', icon: 'calibrate', key: 'C', label: 'Spatial calibration' },
];

function Menu({
  label,
  icon,
  help,
  children,
  testId,
}: {
  label: string;
  icon: string;
  help: string;
  children: ComponentChildren;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  return (
    <div class="menu" ref={ref}>
      <button type="button" class={`tb-btn${open ? ' active' : ''}`} data-help={help} data-testid={testId} onClick={() => setOpen(!open)}>
        <Icon name={icon} />
        <span>{label}</span>
        <Icon name="chevronDown" size={12} />
      </button>
      {open && (
        <div class="menu-pop" onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

function Item({
  onClick,
  children,
  keys,
  checked,
  testId,
}: {
  onClick: () => void;
  children: ComponentChildren;
  keys?: string;
  checked?: boolean;
  testId?: string;
}) {
  return (
    <button type="button" class="menu-item" onClick={onClick} data-testid={testId}>
      <span class="check">{checked === undefined ? '' : checked ? '✓' : ''}</span>
      <span class="grow">{children}</span>
      {keys && <kbd>{keys}</kbd>}
    </button>
  );
}

export function Toolbar() {
  const cap = captureState.value;
  const t = tool.value;
  const l = layout.value;
  return (
    <header class="appbar">
      <div class="brand" data-help="app.about">
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="10" fill="none" stroke="#6aa8ff" stroke-width="1.8" />
          <circle cx="12" cy="12" r="4.5" fill="none" stroke="#ffd166" stroke-width="1.8" />
          <path d="M2 12h20" stroke="#6aa8ff" stroke-width="1.2" opacity=".6" />
        </svg>
        <span>Aperto</span>
      </div>
      <Menu label="File" icon="open" help="menu.file" testId="menu-file">
        <Item onClick={() => openFilePicker('A')} keys="⌘O">
          Open image A…
        </Item>
        <Item onClick={() => openFilePicker('B')}>Open image B (reference)…</Item>
        <Item onClick={() => openFilePicker('dark')}>Open dark frame…</Item>
        <Item onClick={() => openFilePicker('flat')}>Open flat field…</Item>
        <div class="menu-sep" />
        <Item onClick={() => pasteFromClipboard('A')} keys="⌘V">
          Paste image into A
        </Item>
        <div class="menu-sep" />
        <Item onClick={() => saveSession(embedImages.value)} keys="⌘S">
          Save session…
        </Item>
        <Item onClick={loadSessionFile}>Load session…</Item>
        <div class="menu-sep" />
        <Item onClick={() => openPanel('export')} keys="⌘E">
          Export…
        </Item>
      </Menu>
      {!cap.active ? (
        <button type="button" class="tb-btn" data-help="capture.start" onClick={() => startCapture()} data-testid="tb-capture">
          <Icon name="capture" />
          <span>Capture</span>
        </button>
      ) : (
        <button type="button" class="tb-btn accent" data-help="capture.freeze" onClick={() => freezeFrame('A')} data-testid="tb-freeze">
          <Icon name="snapshot" />
          <span>Freeze</span>
          <kbd>G</kbd>
        </button>
      )}
      <div class="tb-sep" />
      <div class="tool-group" role="toolbar" aria-label="Tools">
        {TOOLS.map((x) => (
          <button
            type="button"
            key={x.id}
            class={`tool${t === x.id ? ' on' : ''}`}
            data-help={`tool.${x.id}`}
            data-testid={`tool-${x.id}`}
            aria-pressed={t === x.id}
            aria-label={`${x.label} (${x.key})`}
            onClick={() => (tool.value = x.id)}
          >
            <Icon name={x.icon} size={17} />
          </button>
        ))}
      </div>
      <button
        type="button"
        class={`tool${snapPixels.value ? ' on' : ''}`}
        data-help="tool.snap"
        aria-pressed={snapPixels.value}
        onClick={() => (snapPixels.value = !snapPixels.value)}
      >
        <Icon name="magnet" size={17} />
      </button>
      <div class="tb-sep" />
      <button type="button" class="tool" data-help="history.undo" disabled={!canUndo.value} onClick={undo} aria-label="Undo">
        <Icon name="undo" size={17} />
      </button>
      <button type="button" class="tool" data-help="history.redo" disabled={!canRedo.value} onClick={redo} aria-label="Redo">
        <Icon name="redo" size={17} />
      </button>
      <div class="tb-sep" />
      <button type="button" class="tool" data-help="zoom.fit" onClick={() => viewportApi.current?.fit()} aria-label="Fit">
        <Icon name="fit" size={17} />
      </button>
      <button
        type="button"
        class="tool text"
        data-help="zoom.one"
        onClick={() => viewportApi.current?.oneToOne()}
        aria-label="Actual pixels 1:1"
      >
        1:1
      </button>
      <button type="button" class="tool" data-help="zoom.out" onClick={() => viewportApi.current?.zoomBy(1 / 1.5)} aria-label="Zoom out">
        <Icon name="zoomOut" size={17} />
      </button>
      <button type="button" class="tool" data-help="zoom.in" onClick={() => viewportApi.current?.zoomBy(1.5)} aria-label="Zoom in">
        <Icon name="zoomIn" size={17} />
      </button>
      <span class="spacer" />
      {busy.value && (
        <span class="busy" role="status">
          <span class="spinner" />
          {busy.value}
        </span>
      )}
      <button type="button" class="tb-btn" data-help="panel.calibration" onClick={() => togglePanel('calibration')}>
        <Icon name="calib" />
        <span>Calibrate</span>
      </button>
      <button type="button" class="tb-btn" data-help="panel.analysis" onClick={() => togglePanel('analysis')}>
        <Icon name="chart" />
        <span>Analysis</span>
      </button>
      <button type="button" class="tb-btn" data-help="panel.export" onClick={() => togglePanel('export')} data-testid="tb-export">
        <Icon name="export" />
        <span>Export</span>
      </button>
      <Menu label="Panels" icon="panels" help="menu.panels" testId="menu-panels">
        {Object.entries(PANEL_TITLES).map(([id, title]) => (
          <Item key={id} checked={!!l[id]?.open} onClick={() => togglePanel(id)}>
            {title}
          </Item>
        ))}
        <div class="menu-sep" />
        <Item onClick={resetLayout} testId="reset-layout">
          Reset layout
        </Item>
      </Menu>
      <button type="button" class="tb-btn" data-help="panel.help" onClick={() => togglePanel('help')} data-testid="tb-help">
        <Icon name="help" />
      </button>
    </header>
  );
}
